import { useTranslation } from 'react-i18next';
/**
 * SyncConflictDialog — surfaced when an offline edit collides with a
 * change someone else made on the server while the engineer had no
 * signal. Shows exactly which fields differ and lets the user keep
 * their value on some and the server's on others, then uploads the
 * merged result.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, CloudUpload, Undo2, ChevronDown, ChevronUp, GitMerge,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  listConflicts, resolveConflictKeepMine, resolveConflictKeepTheirs,
  resolveConflictMerge,
} from '../lib/offlineQueue';
import { computeConflictDiff, mergeConflictBody } from '../lib/conflictDiff';
import { dateLocale } from '../lib/dateFormat';

const PREVIEW_ROWS = 8;

const when = (iso) => {
  if (!iso) return 'unknown time';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString(dateLocale());
};

const GRID = 'grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-2';

function ConflictEntry({ entry, busy, onKeepMine, onKeepTheirs, onMerge }) {
  const { t } = useTranslation();
  const snapshot = entry.conflict?.server_snapshot;
  const rows = useMemo(
    () => (snapshot ? computeConflictDiff(entry.body, snapshot, entry.conflict?.labels) : []),
    [entry, snapshot],
  );
  const [selections, setSelections] = useState({});
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const init = {};
    rows.forEach((_, i) => { init[i] = 'mine'; });
    setSelections(init);
  }, [rows]);

  const takeAll = (side) => {
    const next = {};
    rows.forEach((_, i) => { next[i] = side; });
    setSelections(next);
  };
  const serverPicks = rows.filter((_, i) => selections[i] === 'theirs').length;
  const shown = expanded ? rows : rows.slice(0, PREVIEW_ROWS);
  const canMerge = rows.length > 0;

  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid={`conflict-${entry.id}`}>
      <div className="text-sm font-medium">{entry.label || 'Offline change'}</div>
      <p className="text-xs text-muted-foreground">
        Someone saved over this at {when(entry.conflict?.server_updated_at)}.
        You started from the version saved at {when(entry.conflict?.your_base)}.
      </p>

      {!snapshot && (
        <p className="text-xs text-muted-foreground" data-testid={`conflict-no-diff-${entry.id}`}>
          {t('auto.misc.fieldByFieldComparisonIsnT')}
        </p>
      )}
      {snapshot && rows.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid={`conflict-diff-identical-${entry.id}`}>
          No field differences — the two versions hold the same values.
        </p>
      )}

      {rows.length > 0 && (
        <div className="space-y-1.5" data-testid={`conflict-diff-${entry.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] text-muted-foreground">
              Pick a side per field — {serverPicks === 0
                ? 'currently keeping all of your values'
                : `taking ${serverPicks} of ${rows.length} from the server`}
            </span>
            <span className="flex gap-1">
              <Button
                size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                onClick={() => takeAll('mine')}
                data-testid={`conflict-take-all-mine-${entry.id}`}
              >{t('auto.misc.takeAllMine')}</Button>
              <Button
                size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                onClick={() => takeAll('theirs')}
                data-testid={`conflict-take-all-theirs-${entry.id}`}
              >{t('auto.misc.takeAllServer')}</Button>
            </span>
          </div>

          <div className={`${GRID} text-[10px] font-semibold uppercase tracking-wide text-muted-foreground`}>
            <span>{t('auto.misc.field')}</span>
            <span>{t('auto.misc.yourVersion')}</span>
            <span>{t('auto.misc.onTheServer')}</span>
          </div>
          {shown.map((r, i) => {
            const pick = selections[i] || 'mine';
            const cell = (side, text) => (
              <button
                type="button"
                onClick={() => setSelections((prev) => ({ ...prev, [i]: side }))}
                className={`flex items-start gap-1.5 rounded px-1.5 py-1 text-left text-xs break-words border transition-colors ${
                  pick === side
                    ? side === 'mine'
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-800 font-medium'
                      : 'border-sky-300 bg-sky-50 text-sky-800 font-medium'
                    : 'border-transparent text-slate-500 hover:bg-slate-50'
                }`}
                data-testid={`conflict-merge-row-${entry.id}-${i}-${side === 'mine' ? 'mine' : 'theirs'}`}
                data-selected={pick === side}
              >
                <span
                  className={`mt-0.5 h-3 w-3 shrink-0 rounded-full border ${
                    pick === side
                      ? side === 'mine' ? 'border-emerald-600 bg-emerald-600' : 'border-sky-600 bg-sky-600'
                      : 'border-slate-300'
                  }`}
                />
                <span className="min-w-0 break-words">{text}</span>
              </button>
            );
            return (
              <div
                key={`${r.label}-${i}`}
                className={`${GRID} items-start rounded border border-slate-100 bg-slate-50/60 px-1.5 py-1`}
                data-testid={`conflict-diff-row-${entry.id}-${i}`}
              >
                <span className="break-words text-xs text-slate-600 py-1">{r.label}</span>
                {cell('mine', r.mine)}
                {cell('theirs', r.theirs)}
              </div>
            );
          })}
          {rows.length > PREVIEW_ROWS && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
              data-testid={`conflict-diff-toggle-${entry.id}`}
            >
              {expanded
                ? (<><ChevronUp className="h-3 w-3" />{t('auto.misc.showFewer')}</>)
                : (<><ChevronDown className="h-3 w-3" />{`Show all ${rows.length} differences`}</>)}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {canMerge && (
          <Button
            size="sm"
            onClick={() => onMerge(entry, mergeConflictBody(entry.body, rows, selections))}
            disabled={busy}
            data-testid={`conflict-apply-merge-${entry.id}`}
          >
            <GitMerge className="h-3.5 w-3.5 mr-1" />{t('auto.misc.uploadMyPicks')}
          </Button>
        )}
        <Button
          size="sm"
          variant={canMerge ? 'outline' : 'default'}
          onClick={() => onKeepMine(entry)}
          disabled={busy}
          data-testid={`conflict-keep-mine-${entry.id}`}
        >
          <CloudUpload className="h-3.5 w-3.5 mr-1" />{t('auto.misc.keepMine')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onKeepTheirs(entry)}
          disabled={busy}
          data-testid={`conflict-keep-theirs-${entry.id}`}
        >
          <Undo2 className="h-3.5 w-3.5 mr-1" />{t('auto.misc.keepServerVersion')}
        </Button>
      </div>
    </div>
  );
}

export default function SyncConflictDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const [conflicts, setConflicts] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { setConflicts(await listConflicts()); } catch { setConflicts([]); }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const act = async (entry, fn, successMsg) => {
    setBusy(entry.id);
    try {
      await fn();
      toast.success(successMsg);
      const left = await listConflicts();
      setConflicts(left);
      if (left.length === 0) onOpenChange(false);
    } catch {
      toast.error(t('auto.misc.couldNotResolveThatPleaseTry'));
    } finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="sync-conflict-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            {t('auto.misc.changedBySomeoneElse')}
          </DialogTitle>
          <DialogDescription>
            {conflicts.length === 1
              ? 'This was edited on the server while you were offline. Pick which value to keep for each field.'
              : `${conflicts.length} items were edited on the server while you were offline. Pick which value to keep for each field.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {conflicts.length === 0 && (
            <p className="text-sm text-muted-foreground py-2" data-testid="no-conflicts">
              Nothing to resolve — everything is in sync.
            </p>
          )}
          {conflicts.map((c) => (
            <ConflictEntry
              key={c.id}
              entry={c}
              busy={busy === c.id}
              onKeepMine={(e) => act(e, () => resolveConflictKeepMine(e.id), 'Your version has been uploaded')}
              onKeepTheirs={(e) => act(e, () => resolveConflictKeepTheirs(e.id), 'Your offline changes were discarded')}
              onMerge={(e, merged) => act(e, () => resolveConflictMerge(e.id, merged), 'Merged version uploaded')}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
